import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { disableAnalyticsTracking, loadAnalytics } from '@/lib/analytics';
import {
  getCookieConsentState,
  OPEN_COOKIE_SETTINGS_EVENT,
  setCookieConsentState,
  type CookieConsentChoice,
} from '@/lib/cookieConsent';
import './CookieConsentBanner.css';

const focusableSelector =
  'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])';

function demoModalIsOpen() {
  return typeof document !== 'undefined' && Boolean(document.querySelector('[data-demo-modal]'));
}

function focusSurvivingPageTarget() {
  const target = document.getElementById('main-content')
    ?? document.querySelector<HTMLElement>('main')
    ?? document.getElementById('root')
    ?? document.body;

  if (!(target instanceof HTMLElement)) return;

  const previousTabIndex = target.getAttribute('tabindex');
  if (!target.matches(focusableSelector)) {
    target.setAttribute('tabindex', '-1');
  }
  target.focus({ preventScroll: true });
  if (previousTabIndex === null && target.getAttribute('tabindex') === '-1') {
    target.removeAttribute('tabindex');
  } else if (previousTabIndex !== null) {
    target.setAttribute('tabindex', previousTabIndex);
  }
}

export default function CookieConsentBanner() {
  const [isClient, setIsClient] = useState(false);
  const [showBar, setShowBar] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [analyticsDraft, setAnalyticsDraft] = useState(false);
  const [storageError, setStorageError] = useState('');
  const [demoActive, setDemoActive] = useState(false);
  const [deferredPreferencesOpen, setDeferredPreferencesOpen] = useState(false);
  const [clearHeroAction, setClearHeroAction] = useState(false);
  const noticeRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const errorId = `${id}-error`;
  const analyticsLabelId = `${id}-analytics-label`;

  const openPreferences = useCallback(() => {
    if (demoModalIsOpen()) {
      setDeferredPreferencesOpen(true);
      setShowBar(false);
      return;
    }

    const state = getCookieConsentState();
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setAnalyticsDraft(state === 'accepted');
    setStorageError('');
    setShowBar(false);
    setPreferencesOpen(true);
  }, []);

  const closePreferences = useCallback(() => {
    setPreferencesOpen(false);
    setStorageError('');
    if (getCookieConsentState() === 'unknown' && !demoModalIsOpen()) {
      setShowBar(true);
    }
  }, []);

  useEffect(() => {
    setIsClient(true);
    setDemoActive(demoModalIsOpen());
    setShowBar(getCookieConsentState() === 'unknown' && !demoModalIsOpen());
  }, []);

  useEffect(() => {
    if (!isClient || !showBar) return;
    const mobile = window.matchMedia('(max-width: 700px)');
    let frame: number | null = null;
    const updatePlacement = () => {
      frame = null;
      const action = document.querySelector<HTMLElement>('.landing-hero__action button');
      const notice = noticeRef.current;
      if (!action || !notice || !mobile.matches) {
        setClearHeroAction(false);
        return;
      }
      const bounds = action.getBoundingClientRect();
      const card = notice.getBoundingClientRect();
      const style = getComputedStyle(notice);
      const bottomInset = Math.max(parseFloat(style.getPropertyValue('--cookie-bottom-min')) || 8, parseFloat(style.getPropertyValue('--cookie-safe-bottom')) || 0);
      const bottom = window.innerHeight - bottomInset;
      // Test the notice's normal bottom position, even when it is currently up top.
      setClearHeroAction(bounds.width > 0 && bounds.right > card.left && bounds.left < card.right && bounds.bottom > bottom - card.height && bounds.top < bottom);
    };
    const schedule = () => { if (frame === null) frame = requestAnimationFrame(updatePlacement); };
    // The routed landing page may arrive after the shared consent component.
    const arrival = new MutationObserver(schedule);
    arrival.observe(document.getElementById('root') ?? document.body, { childList: true, subtree: true });
    const size = new ResizeObserver(schedule);
    if (noticeRef.current) size.observe(noticeRef.current);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    mobile.addEventListener('change', schedule);
    updatePlacement();
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      arrival.disconnect();
      size.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      mobile.removeEventListener('change', schedule);
    };
  }, [isClient, showBar]);

  useEffect(() => {
    const handleOpenSettings = () => openPreferences();
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
  }, [openPreferences]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const observer = new MutationObserver(() => setDemoActive(demoModalIsOpen()));
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (demoActive) {
      setShowBar(false);
      if (preferencesOpen) {
        setDeferredPreferencesOpen(true);
        setPreferencesOpen(false);
      }
      return;
    }

    if (deferredPreferencesOpen) {
      setDeferredPreferencesOpen(false);
      openPreferences();
      return;
    }

    if (!preferencesOpen && getCookieConsentState() === 'unknown') {
      setShowBar(true);
    }
  }, [deferredPreferencesOpen, demoActive, openPreferences, preferencesOpen]);

  useEffect(() => {
    if (!preferencesOpen || !isClient) return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousScrollbarGutter = html.style.scrollbarGutter;
    const portalRoot = dialog.closest('[data-cookie-modal]');
    const backgroundElements = Array.from(body.children)
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== portalRoot)
      .map(element => ({ element, wasInert: element.inert }));

    html.style.scrollbarGutter = 'stable';
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    backgroundElements.forEach(({ element }) => { element.inert = true; });

    const focusableElements = () => Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector))
      .filter(element => element.getClientRects().length > 0 && !element.closest('[hidden], [inert]'));

    const focusInitialElement = () => {
      const target = dialog.querySelector<HTMLElement>('[data-cookie-autofocus]') ?? focusableElements()[0] ?? dialog;
      target.focus({ preventScroll: true });
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closePreferences();
        return;
      }

      if (event.key !== 'Tab') return;
      const elements = focusableElements();
      const first = elements[0];
      const last = elements[elements.length - 1];
      const active = document.activeElement;

      if (!first || !last) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
      } else if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (event.target instanceof Node && !dialog.contains(event.target)) {
        focusInitialElement();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn);
    focusInitialElement();

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn);
      html.style.overflow = previousHtmlOverflow;
      html.style.scrollbarGutter = previousScrollbarGutter;
      body.style.overflow = previousBodyOverflow;
      backgroundElements.forEach(({ element, wasInert }) => { element.inert = wasInert; });
      const previousFocus = previousFocusRef.current;
      previousFocusRef.current = null;
      const restoredBarSettings = previousFocus?.matches('.oblex-cookie-bar__settings')
        ? document.querySelector<HTMLElement>('.oblex-cookie-bar__settings')
        : null;
      const focusTarget = previousFocus?.isConnected ? previousFocus : restoredBarSettings;
      if (focusTarget) {
        focusTarget.focus({ preventScroll: true });
      } else {
        focusSurvivingPageTarget();
      }
    };
  }, [closePreferences, isClient, preferencesOpen]);

  const persistChoice = (choice: CookieConsentChoice, source: 'bar' | 'dialog') => {
    setStorageError('');
    const saved = setCookieConsentState(choice);

    if (choice === 'accepted') {
      if (!saved) {
        setStorageError('Your browser blocked saving this choice, so analytics has not been enabled.');
        return;
      }
      loadAnalytics();
    } else {
      disableAnalyticsTracking();
      if (!saved) {
        setStorageError('Analytics is off for this visit, but your browser blocked saving this choice.');
        return;
      }
    }

    setShowBar(false);
    setPreferencesOpen(false);
    if (source === 'bar') previousFocusRef.current = null;
  };

  const savePreferences = () => {
    persistChoice(analyticsDraft ? 'accepted' : 'rejected', 'dialog');
  };

  if (!isClient) return null;

  const barVisible = showBar && !preferencesOpen && !demoActive;
  const bar = barVisible ? (
    <div ref={noticeRef} className="oblex-cookie-bar" data-clear-hero={clearHeroAction} role="region" aria-label="Cookie notice">
      <div className="oblex-cookie-bar__inner">
        <p>
          <span className="oblex-cookie-bar__title">Cookies.</span> Optional analytics only runs with your permission.{' '}
          <Link to="/cookies">Cookie Policy</Link>.
        </p>
        <div className="oblex-cookie-bar__actions">
          <button type="button" aria-label="Accept analytics" onClick={() => persistChoice('accepted', 'bar')}>
            Accept
          </button>
          <button type="button" aria-label="Reject analytics" onClick={() => persistChoice('rejected', 'bar')}>
            Reject
          </button>
          <button type="button" className="oblex-cookie-bar__settings" onClick={openPreferences}>
            Settings
          </button>
        </div>
        {storageError && <p className="oblex-cookie-error" role="alert">{storageError}</p>}
      </div>
    </div>
  ) : null;

  const dialog = preferencesOpen ? createPortal(
    <div
      data-cookie-modal
      className="oblex-cookie-backdrop"
      onMouseDown={event => {
        if (event.target === event.currentTarget) closePreferences();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={storageError ? `${descriptionId} ${errorId}` : descriptionId}
        tabIndex={-1}
        className="oblex-cookie-dialog"
      >
        <button
          type="button"
          className="oblex-cookie-dialog__close"
          aria-label="Close cookie preferences"
          onClick={closePreferences}
        >
          ×
        </button>
        <div className="oblex-cookie-dialog__header">
          <p className="oblex-cookie-wordmark" aria-label="Oblex">oblex</p>
          <h2 id={titleId}>Cookie preferences</h2>
          <p id={descriptionId}>
            Essential technologies are always active. Analytics only runs when you save an accepted choice.
          </p>
        </div>
        <div className="oblex-cookie-dialog__rows">
          <div className="oblex-cookie-row">
            <div>
              <p>Essential</p>
              <span>Required for the site to function and remember your choice.</span>
            </div>
            <span className="oblex-cookie-status">Always active</span>
          </div>
          <div className="oblex-cookie-row">
            <div>
              <p id={analyticsLabelId}>Analytics</p>
              <span>Optional Google Analytics helps us understand aggregate website usage.</span>
            </div>
            <button
              type="button"
              role="switch"
              aria-labelledby={analyticsLabelId}
              aria-checked={analyticsDraft}
              data-cookie-autofocus
              className={`oblex-cookie-switch${analyticsDraft ? ' is-on' : ''}`}
              onClick={() => setAnalyticsDraft(value => !value)}
            >
              <span aria-hidden="true" />
            </button>
          </div>
        </div>
        {storageError && <p id={errorId} className="oblex-cookie-error" role="alert">{storageError}</p>}
        <div className="oblex-cookie-dialog__actions">
          <button type="button" className="oblex-cookie-save" onClick={savePreferences}>
            Save preferences
          </button>
          <button type="button" onClick={() => persistChoice('rejected', 'dialog')}>
            Reject analytics
          </button>
          <button type="button" onClick={closePreferences}>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      {bar}
      {dialog}
    </>
  );
}
